import React, { useContext, useEffect } from "react";
import styled from "styled-components";
import { Alert } from "reactstrap";
import Slider from "rc-slider";
import Tooltip from "rc-tooltip";
import Axios from "axios";
import "rc-slider/assets/index.css";

import TokenRow from "../../Components/Main/TokenRow";
import DefaultButton from "../../Components/Common/DefaultButton";
import globalContext from "../../Hooks/globalContext";
import useInput from "../../Hooks/useInput";
import { AMOUNT_REGEX, GAS_PRICE_API_ESC, HOST } from "../../Utils/constants";
import Input from "../../Components/Common/SendInput";
import languagePack from "../../Utils/language";
import useState from "../../Hooks/useState";
import Loader from "../../Components/Common/Loader";
import Theme from "../../Styles/Theme";
import TokenSelect from "../../Components/Common/TokenSelect";
import PageWrapper from "../../Components/Common/PageWrapper";
import PinCode from "../../Components/PinCodeOTP/PinCode";
import PinCodeRegister from "../../Components/PinCodeOTP/Register";

const Container = styled.div`
  width: 100%;
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const Form = styled.div`
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 5px;
`;

const QrForm = styled(Form)`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0px 5px;
  margin-bottom: 3px;
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 10px;

  font-size: 14px;
  color: ${(props) => props.theme.black};
  font-weight: bold;
  :hover {
    opacity: 0.75;
    text-decoration: underline;
  }
`;

const SliderForm = styled(Form)`
  background-color: rgba(0, 0, 0, 0);
  width: 100%;
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  span {
    white-space: nowrap;
  }
`;

const FeeLabel = styled.div`
  width: 100%;
  color: grey;
  font-size: 12px;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  padding: 15px;
`;

const NotificationWrapper = styled.div`
  width: 100%;
  padding: 0px 15px;
  font-size: 14px;
`;

const Handle = Slider.Handle;

const Page = ({ history }) => {
  const { user, language, balance, modal, sendEth, sendToken, web3, contract, httpHeader } = useContext(globalContext);
  const amountInput = useInput({ defaultValue: "", regex: AMOUNT_REGEX });
  const toInput = useInput({ defaultValue: "", maxLength: 42 });
  const fee = useState(null);
  const selected = useState("eth");

  const isLoading = useState(false);
  const gas = useState(null);
  const marks = useState({});

  const code = useState(null);

  const pin = useState("");
  const pinLoading = useState(false);
  const error = useState(null);

  const authentication = async () => {
    const { data } = await Axios.post(`${HOST}/user/otp/validation`, { pin: pin.state }, httpHeader());
    if (data.status) {
      const confirm = () => {
        amountInput.setValue("");
        toInput.setValue("");
        code.setState(null);
        pin.setState("");
        pinLoading.setState(false);
        getGas();
        modal.setState(languagePack("성공적으로 처리되었습니다.", language.state));
      };

      const reject = () => {
        code.setState(null);

        pin.setState("");
        pinLoading.setState(false);
        getGas();
        modal.setState(languagePack("전송 오류입니다.", language.state));
      };

      const sendForm = {
        address: user.state.address,
        fee: fee.state,
        to: toInput.value,
        amount: amountInput.value,
        isLoading,
        confirm,
        reject,
        symbol: selected.state,
      };
      isLoading.setState(true);
      switch (selected.state) {
        case "eth":
          await sendEth(sendForm);
          return;
        default:
          await sendToken(sendForm);
          return;
      }
    } else {
      pinLoading.setState(false);
      switch (data.code) {
        case "blocked":
          error.setState(languagePack("잠금상태입니다. 잠시 후에 다시 시도해주세요.", language.state));
          break;
        case "block":
          error.setState(languagePack("비밀번호 3회 오류로 5분간 송금이 제한됩니다.", language.state));
          break;
        case "invalid":
          error.setState(languagePack("잘못된 OTP 인증번호입니다.", language.state));
          break;
        case "server":
          error.setState(languagePack("서버 오류입니다. 잠시 후에 다시 시도해주세요.", language.state));
          break;
      }
    }
  };

  useEffect(() => {
    if (pin.state.length > 5) {
      pin.setState("");
      pinLoading.setState(true);
      authentication();
    }
  });

  const sendOnClick = async () => {
    if (toInput.value.length !== 42) {
      return modal.setState(languagePack("올바른 지갑주소가 아닙니다.", language.state));
    }
    if (!web3.utils.isAddress(toInput.value)) {
      return modal.setState(languagePack("올바른 지갑주소가 아닙니다.", language.state));
    }
    if (amountInput.value === "") {
      return modal.setState(languagePack("보낼 금액을 입력하세요.", language.state));
    }
    if (Number(amountInput.value) === 0) {
      return modal.setState(languagePack("보낼 금액을 입력하세요.", language.state));
    }

    if (selected.state === "eth" && Number(balance.state.eth) < Number(amountInput.value)) {
      return modal.setState(languagePack("잔액이 부족합니다.", language.state));
    } else if (selected.state === "lcms" && Number(balance.state.lcms) < Number(amountInput.value)) {
      return modal.setState(languagePack("잔액이 부족합니다.", language.state));
    } else if (selected.state === "usdt" && Number(balance.state.usdt) < Number(amountInput.value)) {
      return modal.setState(languagePack("잔액이 부족합니다.", language.state));
    }

    if (selected.state === "lcms") {
      const {
        data: { locked, staking, status },
      } = await Axios.post(`${HOST}/api/available/token`, { to: toInput.value, type: selected.state }, httpHeader());

      if (!status) {
        return modal.setState("External transfer blocked");
      }
      if (
        Number(balance.state.lcms) - Number(locked.sum - locked.available_amount) - Number(staking.sum) <
        Number(amountInput.value)
      ) {
        return modal.setState(languagePack("잔액이 부족합니다.", language.state));
      }
    } else if (selected.state === "esg") {
      const {
        data: { locked, staking, status },
      } = await Axios.post(`${HOST}/api/available/token`, { to: toInput.value, type: selected.state }, httpHeader());

      if (!status) {
        return modal.setState("External transfer blocked");
      }

      if (
        Number(balance.state.esg) - Number(locked.sum - locked.available_amount) - Number(staking.sum) <
        Number(amountInput.value)
      ) {
        return modal.setState(languagePack("잔액이 부족합니다.", language.state));
      }
    }

    const { data } = await Axios.post(`${HOST}/user/otp/check`, null, httpHeader());
    if (data.isNull === 1) {
      code.setState(2);
    } else {
      code.setState(1);
    }
  };

  const handleSlider = ({ value, dragging, index, ...restProps }) => {
    fee.setState(value);
    return (
      <Tooltip prefixCls="rc-slider-tooltip" overlay={value} visible={dragging} placement="top" key={index}>
        <Handle value={value} {...restProps} />
      </Tooltip>
    );
  };

  const inputAll = () => {
    switch (selected.state) {
      case "eth":
        amountInput.setValue(balance.state.eth);
        break;
      case "lcms":
        amountInput.setValue(balance.state.lcms);
        break;
      case "usdt":
        amountInput.setValue(balance.state.usdt);
        break;
    }
  };

  const getGas = async () => {
    const { data: esc } = await Axios.get(GAS_PRICE_API_ESC);
    const data = {
      safeLow: esc.result.SafeGasPrice * 10,
      average: esc.result.ProposeGasPrice * 10,
      fast: esc.result.FastGasPrice * 10,
      fastest: esc.result.FastGasPrice * 10,
    };

    gas.setState(data);
    const mark = {};
    mark[data.safeLow] = languagePack("느림", language.state);
    mark[data.average] = languagePack("보통", language.state);
    mark[data.fast] = languagePack("빠름", language.state);
    marks.setState(mark);
  };

  const preload = () => {
    getGas();
  };

  useEffect(preload, []);
  return code.state ? (
    <>
      {code.state === 1 && (
        <PinCode
          goBack={() => {
            history.goBack();
          }}
          error={error.state}
          isLoading={pinLoading.state}
          pin={pin}
          title={languagePack("OTP 인증번호를 입력하세요.", language.state)}
        />
      )}
      {code.state === 2 && <PinCodeRegister />}
    </>
  ) : (
    <PageWrapper>
      <Wrapper>
        <Container>
          {balance.state && <TokenSelect selected={selected} />}
          {balance.state && (
            <>
              {selected.state === "eth" && (
                <TokenRow balance={balance.state.eth} symbol="eth" fullName="Ethereum" send={true} />
              )}
              {selected.state === "esg" && (
                <TokenRow balance={balance.state.esg} symbol="esg" fullName="Energy Solution Global" send={true} />
              )}
              {selected.state === "lcms" && (
                <TokenRow balance={balance.state.lcms} symbol="lcms" fullName="LCM Science" send={true} />
              )}
              {selected.state === "usdt" && (
                <TokenRow balance={balance.state.usdt} symbol="usdt" fullName="Tether USD" send={true} />
              )}
            </>
          )}
          <QrForm>
            <Input {...amountInput} placeholder={languagePack("보낼 금액", language.state)} />
            <IconWrapper onClick={inputAll}>{languagePack("최대", language.state)}</IconWrapper>
          </QrForm>
          <QrForm>
            <Input {...toInput} placeholder={languagePack("받는 지갑주소", language.state)} />
          </QrForm>
          <QrForm>
            <SliderForm>
              <FeeLabel>{languagePack("수수료", language.state)}</FeeLabel>
              {gas.state ? (
                <Slider
                  style={{ width: "85vw", marginBottom: 20, backgroundColor: "rgba(255,255,255,0)" }}
                  trackStyle={{ backgroundColor: Theme.mainColor }}
                  dotStyle={{ borderColor: Theme.mainColor }}
                  handleStyle={{ borderColor: Theme.mainColor }}
                  handle={handleSlider}
                  marks={marks.state}
                  min={gas.state.safeLow}
                  defaultValue={gas.state.average}
                  max={gas.state.fast}
                />
              ) : (
                <Loader fit={true} color={Theme.darkGrey} size={15} />
              )}
            </SliderForm>
          </QrForm>
          <ButtonWrapper>
            <DefaultButton
              disabled={isLoading.state}
              onClick={sendOnClick}
              text={languagePack("송금", language.state)}
            />
          </ButtonWrapper>
          <NotificationWrapper>
            {gas.state && fee.state < gas.state.safeLow + (gas.state.average - gas.state.safeLow) / 2 && (
              <Alert color="danger">
                {languagePack("수수료가 너무 낮으면 트랜잭션이 거부될 수 있습니다.", language.state)}
              </Alert>
            )}
          </NotificationWrapper>
        </Container>
      </Wrapper>
    </PageWrapper>
  );
};

export default Page;
